import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck, AlertTriangle, Unlock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import AuthImage from "@/components/AuthImage";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" });
}

export default function AdminDM() {
  const [tab, setTab] = useState("conversations");
  const [convos, setConvos] = useState([]);
  const [violations, setViolations] = useState([]);
  const [muted, setMuted] = useState([]);
  const [active, setActive] = useState(null); // {pair_key, user_a, user_b}
  const [msgs, setMsgs] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [c, v, m] = await Promise.all([
        api.get("/admin/dm/conversations"),
        api.get("/admin/dm/violations"),
        api.get("/admin/dm/muted"),
      ]);
      setConvos(c.data);
      setViolations(v.data);
      setMuted(m.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openConvo = async (c) => {
    setActive(c);
    setMsgs([]);
    try {
      const r = await api.get(`/admin/dm/messages?pair_key=${encodeURIComponent(c.pair_key)}`);
      setMsgs(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const unmute = async (uid) => {
    if (!window.confirm("Lift this member's DM mute now?")) return;
    try {
      await api.post(`/admin/dm/unmute/${uid}`);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} strokeWidth={1.5} className="text-[#d4af37]" />
          <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">DM Oversight</p>
        </div>
        <Link to="/admin" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#d4af37] flex items-center gap-1" data-testid="back-admin">
          <ArrowLeft size={14} strokeWidth={1.5} /> Concierge
        </Link>
      </div>
      <h1 className="font-serif text-4xl">All private conversations.</h1>
      <p className="text-white/50 text-sm">
        Read-only oversight. House rules enforce: only admins may share contact info; no sexual content; 7-day mute on any violation.
      </p>

      <div className="flex gap-1 border-b border-white/10">
        {[
          ["conversations", `Threads · ${convos.length}`],
          ["violations", `Violations · ${violations.length}`],
          ["muted", `Muted · ${muted.length}`],
        ].map(([t, label]) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setActive(null);
            }}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] -mb-px border-b ${
              tab === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50"
            }`}
            data-testid={`admindm-tab-${t}`}
          >
            {label}
          </button>
        ))}
      </div>

      {err && <p className="text-red-400 text-sm" data-testid="admindm-error">{err}</p>}

      {tab === "conversations" && (
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 min-h-[60vh]">
          <div className={`border border-white/10 rounded-md overflow-hidden ${active ? "hidden md:block" : ""}`}>
            {convos.length === 0 ? (
              <p className="p-6 text-white/40 text-sm" data-testid="admindm-empty">No DM activity yet.</p>
            ) : (
              <div className="divide-y divide-white/5" data-testid="admindm-convo-list">
                {convos.map((c) => (
                  <button
                    key={c.pair_key}
                    onClick={() => openConvo(c)}
                    className={`w-full text-left p-4 hover:bg-white/[0.03] transition ${
                      active?.pair_key === c.pair_key ? "bg-white/[0.05]" : ""
                    }`}
                    data-testid={`admindm-convo-${c.pair_key}`}
                  >
                    <div className="font-medium truncate">
                      {c.user_a?.name || "?"} ↔ {c.user_b?.name || "?"}
                    </div>
                    <div className="text-xs text-white/40 truncate mt-1">{c.last_text}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/30 mt-1">
                      {c.msg_count} msgs · {fmt(c.last_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={`border border-white/10 rounded-md flex flex-col ${active ? "" : "hidden md:flex"}`}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Select a conversation</div>
            ) : (
              <>
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <button onClick={() => setActive(null)} className="md:hidden text-white/60" data-testid="admindm-back">
                    <ArrowLeft size={18} strokeWidth={1.5} />
                  </button>
                  <div className="font-medium">
                    {active.user_a?.name} ↔ {active.user_b?.name}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="admindm-thread">
                  {msgs.length === 0 ? (
                    <p className="text-white/40 text-sm text-center mt-12">No messages.</p>
                  ) : (
                    msgs.map((m) => {
                      const mineSide = m.sender_id === active.user_a?.id;
                      return (
                        <div key={m.id} className={`flex ${mineSide ? "justify-start" : "justify-end"}`}>
                          <div
                            className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm border ${
                              mineSide
                                ? "bg-[#141414] border-white/10 text-white/90 rounded-bl-sm"
                                : "bg-[#1a1a1a] border-[#d4af37]/30 text-white/90 rounded-br-sm"
                            }`}
                          >
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                              {m.sender_name || (mineSide ? active.user_a?.name : active.user_b?.name)}
                            </div>
                            {m.image_path && (
                              <AuthImage
                                path={m.image_path}
                                className="rounded-lg mb-1.5 max-h-60 object-contain bg-black/30"
                              />
                            )}
                            {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                            <div className="text-[10px] text-white/40 mt-1">{fmt(m.created_at)}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "violations" && (
        <div className="space-y-2" data-testid="admindm-violations">
          {violations.length === 0 && <p className="text-white/40">No violations logged.</p>}
          {violations.map((v) => (
            <div key={v.id} className="border border-white/10 rounded-md p-4 bg-[#141414]">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm">
                    <span className="font-medium">{v.user_name}</span>
                    <span className="text-white/40"> · {v.reason === "contact_info" ? "shared contact info" : "sexual content"}</span>
                  </div>
                  <div className="text-white/70 text-sm mt-2 italic">"{v.message_text}"</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
                    {fmt(v.created_at)} · banned until {fmt(v.ban_until)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "muted" && (
        <div className="space-y-2" data-testid="admindm-muted">
          {muted.length === 0 && <p className="text-white/40">No members are currently muted.</p>}
          {muted.map((u) => (
            <div key={u.id} className="border border-white/10 rounded-md p-4 bg-[#141414] flex items-center justify-between">
              <div>
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-white/50 mt-0.5">
                  Muted until <span className="text-red-300">{fmt(u.chat_banned_until)}</span>
                </div>
              </div>
              <button
                onClick={() => unmute(u.id)}
                className="flex items-center gap-2 border border-white/10 hover:border-[#d4af37]/60 px-3 py-2 text-xs uppercase tracking-widest text-white/70 hover:text-[#d4af37]"
                data-testid={`admindm-unmute-${u.id}`}
              >
                <Unlock size={14} strokeWidth={1.5} />
                Unmute
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
